import {compareQuestionOrder} from '~/helpers/questions'
import {useAxiosClient} from "~/use/axios/client";
import {
    type GMServicesCommonQuestionsQuestionDefinition,
    type GMServicesCommonQuestionsQuestionTemplate,
    GMServicesCommonQuestionsQuestionTemplateType,
    QuestionApi
} from "~/gen/openapi/sblService";
import {AllmaActionType} from "~/constants/allmaActionType";
import {MS_HOUR} from "~/constants/time";
import {keepPreviousData, useQuery} from "@tanstack/vue-query";
import type {FullContract} from "~/models/contract";
import {getSalesOrderTypeFromAllmaActionType, getSalesOrderTypeFromAllmaJobType} from "~/constants/salesOrder";

export const useQuestionService = () => {
    const {axiosClient} = useAxiosClient()
    const questionApi = new QuestionApi(undefined, '', axiosClient.value)

    const defaultQueryOptions = {
        staleTime: MS_HOUR,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        gcTime: Infinity,
    }

    const getQuestionTemplatesQueryOptions = {
        queryKey: ['questionGetAllQuestionTemplates'],
        queryFn: async () => (await questionApi.questionGetAllQuestionTemplates())?.data,
        ...defaultQueryOptions
    }
    const questionTemplatesQuery = () => useQuery(getQuestionTemplatesQueryOptions)

    const getQuestionDefinitionsQueryOptions = {
        queryKey: ['questionGetAllQuestionDefinitions'],
        queryFn: async () => (await questionApi.questionGetAllQuestionDefinitions())?.data,
        ...defaultQueryOptions
    }

    const questionDefinitionsQuery = () => useQuery(getQuestionDefinitionsQueryOptions)


    const getQuestionDefinitionById = (questionDefinitions: Array<GMServicesCommonQuestionsQuestionDefinition>, id: string) => {
        return questionDefinitions?.find((question) => question.Id === id)
    }

    const sortQuestionDefinitionsByOrder = (questionDefinitions: Array<GMServicesCommonQuestionsQuestionDefinition>) => {
        // Copy first: on the single-template path this array is the query cache's own
        return [...questionDefinitions].sort(compareQuestionOrder)
    }

    const getQuestionTemplateByType = (questionTemplates: Array<GMServicesCommonQuestionsQuestionTemplate | undefined>, type: number, contract?: FullContract) => {
        const activeAllmaJobs = contract?.AllmaJobs?.filter((aj) => !aj.Removed)
        const allmaJobTypesInContract = activeAllmaJobs?.map((aj) => aj.JobType).filter((jobType) => !!jobType) as string[] | undefined
        const silvicultureActionPlansInContract: AllmaActionType[] = activeAllmaJobs?.flatMap(
            aj => aj.Actions?.flatMap(
                a => a.SilviCultureActionPlans?.flatMap(
                    s => s.Type as AllmaActionType
                )))?.filter(t => t !== undefined) || []

        // A type Allma sends that the app does not know yet is skipped, so the other types still select their templates
        const toSalesOrderType = (convert: () => number): number[] => {
            try {
                return [convert()]
            } catch {
                return []
            }
        }
        const salesOrderTypesInContract = [
            ...(allmaJobTypesInContract ?? []).flatMap((ajt) => toSalesOrderType(() => getSalesOrderTypeFromAllmaJobType(ajt))),
            ...silvicultureActionPlansInContract.flatMap((plan) => toSalesOrderType(() => getSalesOrderTypeFromAllmaActionType(plan))),
        ]

        // An empty ContractTypes list means the template applies to every contract type
        const matchesContractType = (template?: GMServicesCommonQuestionsQuestionTemplate) => {
            if (!template?.ContractTypes?.length) {
                return true
            }
            return contract?.Type !== undefined && template.ContractTypes.includes(contract.Type)
        }

        const templates = questionTemplates?.filter((template) => {
            return template?.QuestionTemplateType as unknown as number === type
                && matchesContractType(template)
                && template?.OrderTypes?.some((ot) => salesOrderTypesInContract?.includes(ot))
        })

        if (templates.length > 1) {
            // Matching templates are additive, and the same definition can appear in several
            const seenDefinitionIds = new Set<string>()
            const questionDefinitions: Array<GMServicesCommonQuestionsQuestionDefinition> = []

            templates.forEach((matchedTemplate) => {
                matchedTemplate?.QuestionDefinitions?.forEach((definition) => {
                    if (!definition.Id || seenDefinitionIds.has(definition.Id)) {
                        return
                    }
                    seenDefinitionIds.add(definition.Id)
                    questionDefinitions.push(definition)
                })
            })

            return {
                Id: undefined,
                Name: null,
                QuestionTemplateType: type as unknown as GMServicesCommonQuestionsQuestionTemplateType,
                QuestionDefinitions: questionDefinitions,
                OrderTypes: null
            } as GMServicesCommonQuestionsQuestionTemplate
        }
        return templates[0]
    }

    return {
        getQuestionTemplatesQueryOptions,
        questionTemplatesQuery,
        getQuestionDefinitionsQueryOptions,
        questionDefinitionsQuery,
        getQuestionDefinitionById,
        sortQuestionDefinitionsByOrder,
        getQuestionTemplateByType,
    }
}